import { Component, ElementRef, HostListener, Input, NO_ERRORS_SCHEMA, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import _ = require('lodash');
import { Subscription } from 'rxjs/Subscription';
import { RemittanceEntry } from '../../../models/remittance-entry.model';
import { RemittanceSummary } from '../../../models/remittance-summary.model';
import { AuthorizationService } from '../../../services/authorization.service';
import { GlobalFilterService } from '../../../services/global-filter.service';
import { RemittanceEntryService } from '../../../services/remittance-entry/remittance-entry.service';
import { RemittanceSummaryService } from '../../../services/remittance-entry/remittance-summary.service';
import { RemittanceUnapprovedService } from "../../../services/remittance-unapproved.service";
import { UnapprovedRemittance } from "../../../models/unapproved-remittance.model";

const DEPOSIT_SEARCH_ROUTE = 'depositsearch'
// Percent of invalid remitts at which a batch apply requires confirmation
const BATCH_APPLY_PERC = .15
const MODAL_COMMENT_LIMIT = 400

@Component({
  selector: 'app-remittance-summary',
  templateUrl: './remittance-summary.component.html',
  styleUrls: ['./remittance-summary.component.css'],
})

//  This is a Smart component.
export class RemittanceSummaryComponent implements OnInit, OnDestroy {
  @ViewChild('modalaccept') modalAccept: ElementRef;
  @ViewChild('modal') modal: ElementRef;
  @ViewChild('modalcomment') modalComment: ElementRef;
  @ViewChild('modalcancel') modalCancel: ElementRef;
  //  Subscriptions
  onRemittanceSummary: Subscription;
  onUserDetails: Subscription;

  //  Members
  private _remittanceSummary: RemittanceSummary;
  displayModal: boolean;
  modalHeader: string;
  modalMessage: string;
  modalAcceptText: string;
  modalFocused: boolean = false;
  modalCommentText: string;
  modalRemainingText: number = MODAL_COMMENT_LIMIT;
  isInternalUser: boolean;
  showZeroDollarCheckLabel: boolean = false;

  get remittanceSummary(): RemittanceSummary {
    return this._remittanceSummary;
  }

  get hasMREOverride(): boolean {
    return this.authorizationService.hasFeature("UI::MREOverride");
  }


  @Input() checkRefNumber: string;
  @Input() initialCheckValue: string;
  @Input() checkNumber: string;
  @Input() carrierId: string;
  @Input() depositDate: string;
  @Input() checkStatus: string;
  //  Form controls
  formLoading: boolean

  constructor(
    private authorizationService: AuthorizationService,
    private globalFilterService: GlobalFilterService,
    private route: ActivatedRoute,
    private remittanceEntryService: RemittanceEntryService,
    private remittanceSummaryService: RemittanceSummaryService,
    private unapprovedRemittancesService: RemittanceUnapprovedService,
    private router: Router,
  ) {
    //  Subscribe to the Remittance Entry notification.
    this.onRemittanceSummary = remittanceEntryService.remittanceSummarySource$.subscribe(
      (summary) => {
        this._remittanceSummary = summary;
        this.formLoading = false;
      },
      (error) => {
        console.log(`Error occurred at ${new Date()} status: ${error.status.toString()}`)
      },
      () => { },   //  Complete
    );
    this.onUserDetails = this.authorizationService.CurrentUser().subscribe(
      (user) => {
        this.isInternalUser = user.UserName.toLowerCase().startsWith("inm\\") || user.UserName.toLowerCase().endsWith("@inmar.com");
      },
      (msg) => {
        console.log(msg);
      },
      () => { },
    );
  }

  ngAfterViewChecked(): void {
    // If the popup should be displayed, foucs the submit button.
    // If the user should see the comment box, allow that to be focused OR the submit button to be focused
    if (this.displayModal &&
      ((this.hasMREOverride || !this.isInternalUser ) || (this.modalComment.nativeElement !== document.activeElement && this.modalAccept.nativeElement !== document.activeElement))) {
      this.modalAccept.nativeElement.focus();
    }
    if (this.displayModal && this.isInternalUser && !this.hasMREOverride) {
      // should see comment box
      if (this.modalComment.nativeElement !== document.activeElement && this.modalCancel.nativeElement !== document.activeElement && this.modalAccept.nativeElement !== document.activeElement) {
        this.modalCancel.nativeElement.focus();
      }
    }
  }

  ngOnInit() {
    this._remittanceSummary = new RemittanceSummary();
    this.formLoading = true;
    if(this.checkStatus.includes("MRZ")){
      this.showZeroDollarCheckLabel = true;
    }
  }

  ngOnDestroy(): void {
    this.onRemittanceSummary.unsubscribe();
  }

  async refreshRemittanceSummary() {
    this.formLoading = true;
    this.callNotifyService();
    await this.remittanceSummaryService.getRemittanceSummary(this.globalFilterService.selectedEnvironment.ClientId4Digit, parseInt(this.checkRefNumber)).then((summary) => {
      if (!summary) {
        summary = new RemittanceSummary();
        summary.DepositAmount = parseFloat(this.initialCheckValue);
        summary.Variance = parseFloat(this.initialCheckValue);
      }
      this._remittanceSummary = summary;
      this.formLoading = false;
    })
    .catch((error) => {
      toastr.error('An error occurred while refreshing the remittance summary. If this problem continues please contact your account team.');
      this.formLoading = false;
    });
  }

  async applyRemittancesToDeposit() {
    this.formLoading = true;
    this.callNotifyService();
    await this.remittanceSummaryService.submitRemittanceBatch(this.globalFilterService.selectedClientId, parseInt(this.checkRefNumber)).then((success) => {
      if (success === true) {
        toastr.success('Batch successfully applied.');
        this.formLoading = false;
        this.router.navigateByUrl(DEPOSIT_SEARCH_ROUTE);
      } else {
        toastr.error('Variance does not equal 0. Batch application to deposit failed.');
        this.refreshRemittanceSummary();
      }
    })
    .catch((error) => {
      toastr.error('An error occurred while applying batch to the deposit. If this problem continues please contact your account team.');
      this.formLoading = false;
    });
  }

  applyModal() {
    // Check to see if percentage of invalid claims is within acceptable ranges
    let batch_perc: number = this._remittanceSummary.UnvalidatedPostedClaims / this._remittanceSummary.RemittanceRecordCount;
    if (batch_perc > BATCH_APPLY_PERC) {
      this.modalHeader = "Apply Batch";
      this.modalMessage = `${(batch_perc * 100).toFixed()}% of your batch has unmatched claims.\n`

      if (!this.hasMREOverride && this.isInternalUser) {
        this.modalAcceptText = "Submit to Supervisor"
        this.modalMessage += `Click \"Cancel\" to continue or "${this.modalAcceptText}" to have a supervisor review.`;
      } else{
        this.modalAcceptText = "OK"
        this.modalMessage += `Click "${this.modalAcceptText}" to continue with the batch job.`;
      }

      this.displayModal = true;
    } else {
      this.applyRemittancesToDeposit();
    }
  }

  // Modal has been accepted
  okClick() {
    this.displayModal = false;
    if (!this.hasMREOverride && this.isInternalUser) {
      var remit = new UnapprovedRemittance();
      remit.CarrierId = this.carrierId;
      remit.CheckRef = parseInt(this.checkRefNumber);
      remit.CheckNum = this.checkNumber;
      remit.CheckAmount = parseFloat(this.initialCheckValue);
      remit.Comment = this.modalComment.nativeElement.value;
      remit.DepositDate = new Date(this.depositDate);
      this.unapprovedRemittancesService.requestApproval(this.globalFilterService.selectedClientId, remit).then((success) => {
        toastr.success("Successfully submitted batch!");
        this.router.navigateByUrl(DEPOSIT_SEARCH_ROUTE);
      });
    } else {
      toastr.info("Applying batch");
      this.applyRemittancesToDeposit();
    }
  }

  // Update character counter
  modalValueChange(value: string) {
    this.modalRemainingText = MODAL_COMMENT_LIMIT - value.length;
  }

  // Modal has been canceled
  cancelClick() {
    this.displayModal = false;
  }

  // Style invalid rows
  invalidCountStyle() {
    if (this._remittanceSummary.UnvalidatedPostedClaims / this._remittanceSummary.RemittanceRecordCount > BATCH_APPLY_PERC) {
      return "text-danger";
    } else {
      return "";
    }
  }

  private callNotifyService() {
    if (!this.authorizationService.HealthcareTopBar || !this.authorizationService.HealthcareTopBar.notifyActivity) {
      return;
    }
    this.authorizationService.HealthcareTopBar.notifyActivity();
  }
}



// WEBPACK FOOTER //
// ./src/app/components/remittance-entry/remittance-summary/remittance-summary.component.ts